import { getDoc, getDocs } from 'firebase/firestore';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import Dropzone from 'react-dropzone';
import { useCollection } from 'react-firebase-hooks/firestore';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Context } from '../../index';

import ChatList from './components/ChatList';
import ChatListHeader from './components/ChatListHeader';
import Gallery from './components/Gallery';
import GroupInfo from './components/GroupInfo';
import MessageInput from './components/MessageInput';
import MessageListBody from './components/MessageListBody';
import MessageListHeader from './components/MessageListHeader';
import useGalleryControls from './hooks/useGalleryControls';
import useSendMessage from './hooks/useSendMessage';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background: white;
  width: 100%;
`;

const ChatListSider = styled.div`
  display: flex;
  flex-direction: column;
  width: 390px;
  height: 100vh;
  border-right: 1px solid #d7def3;
`;

const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100vh;
  transition: all 0.3s;
  position: relative;
`;

const DropView = styled.div`
  position: absolute;
  top: 30px;
  left: 30px;
  right: 30px;
  bottom: 30px;
  background: white;
  border: 2px solid #397cf3;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
`;

const DropText = styled.p`
  font-size: 22px;
  font-weight: bold;
  font-family: 'Open Sans', sans-serif;
`;

const DropContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: calc(100vh - 64px);
`;

const GroupInfoContainer = styled.div`
  display: flex;
  height: 100vh;
  transition: width 0.3s, opacity 0.01s;
`;

const Static = styled.div`
  display: flex;
  flex: 1;
  width: calc(100% - 290px);
  transition: width 0.5s;
`;

const Chat = () => {
  const [isGroupInfoVisible, setIsGroupInfoVisible] = useState(false);
  const [chatRooms, setChatRooms] = useState([]);
  const [isChatRoomsLoading, setIsChatRoomsLoading] = useState(true);
  const { isGalleryVisible, showGallery, closeGallery, galleryImagePath } = useGalleryControls();
  const [activeChat, setActiveChat] = useState(null);
  const [replyMessage, setReplyMessage] = useState(null);
  const [groupUsers, setGroupUsers] = useState([]);
  const { firestore, auth } = useContext(Context);
  const user = auth.currentUser;
  const userId = user?.uid;
  const { sendFile } = useSendMessage({
    activeChat,
    userId,
  });

  const [chatRoomSnapshot, loading] = useCollection(
    firestore
      .collection('chat_rooms')
      .where('user_ids', 'array-contains', userId)
      .where('room_type', 'in', [0, 2]),
  );

  const { id } = useParams();
  useEffect(() => id && setActiveChat(id), [id]);

  useEffect(() => {
    const getChatRooms = async () => {
      const asyncChatRoomSnapshot =
        chatRoomSnapshot?.docs?.map(async (doc) => {
          const _data = doc.data();
          let chatName = _data?.room_name || '';
          let avatarUrl = _data?.room_avatar || '';
          if (_data?.room_type === 0) {
            const otherUserId = _data?.user_ids.find((item) => item !== userId);
            if (otherUserId) {
              const otherUserSnapshot = await getDoc(
                firestore.collection('users').doc(otherUserId),
              );
              const otherUserFullProfileSnapshot = await getDoc(
                firestore.collection('user_full_profile').doc(otherUserId),
              );
              const otherUser = otherUserSnapshot.data();
              const otherUserFullProfile = otherUserFullProfileSnapshot.data();
              chatName = `${otherUser?.first_name} ${otherUser?.last_name}` || 'Unknown user';
              avatarUrl = otherUserFullProfile?.avatar?.avatar_path || '';
            }
          }
          return {
            id: doc.id,
            data: {
              ..._data,
              chatName,
              avatarUrl,
            },
          };
        }) || [];
      try {
        const _chatRooms = await Promise.all(asyncChatRoomSnapshot);
        const sorted = _chatRooms.sort(
          (a, b) => b.data.last_msg_epoch.seconds - a.data.last_msg_epoch.seconds,
        );
        setChatRooms(sorted);
        setIsChatRoomsLoading(false);
      } catch (e) {
        setIsChatRoomsLoading(false);
      }
    };

    getChatRooms();
  }, [chatRoomSnapshot]);

  const isChatListLoading = isChatRoomsLoading || loading;
  const selectChat = (id) => setActiveChat(id);
  const isGroupChat = useMemo(() => {
    const currentChat = chatRooms.find((item) => item.id === activeChat);
    const isGroup = currentChat?.data?.room_type === 2;
    setIsGroupInfoVisible(isGroup);
    if (!isGroup) setGroupUsers([]);
    return isGroup;
  }, [chatRooms, activeChat]);

  useEffect(() => {
    const getGroupUsers = async () => {
      if (isGroupChat) {
        const currentChat = chatRooms.find((item) => item.id === activeChat);
        if (currentChat) {
          // eslint-disable-next-line camelcase
          const user_ids = currentChat.data.user_ids || [];
          const snapshot = await getDocs(
            firestore.collection('users').where('firebase_uid', 'in', user_ids),
          );
          const result = snapshot.docs.map((item) => item.data()) || [];
          if (result?.length) {
            setGroupUsers(result);
          }
        }
      }
    };

    getGroupUsers();
  }, [isGroupChat]);

  const selectedChatName = useMemo(() => {
    let chatName = 'Select chat';
    if (activeChat) {
      const selectedChatRoom = chatRooms.find((item) => item.id === activeChat);
      if (selectedChatRoom) chatName = selectedChatRoom.data.chatName;
    }
    return chatName;
  }, [activeChat, chatRooms]);

  useEffect(() => {
    if (replyMessage) setReplyMessage(null);
  }, [activeChat]);

  const handleDrop = (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    file && activeChat && sendFile(file);
  };

  return (
    <Container>
      <Static>
        <ChatListSider>
          <ChatListHeader />
          <ChatList
            chatRooms={chatRooms}
            isChatListLoading={isChatListLoading}
            selectChat={selectChat}
            activeChat={activeChat}
          />
        </ChatListSider>
        <MessageList>
          <MessageListHeader selectedChatName={selectedChatName} isGroupChat={isGroupChat} />
          <Dropzone noClick onDrop={handleDrop}>
            {({ isDragActive, getRootProps, getInputProps }) => (
              <DropContainer {...getRootProps()}>
                {isDragActive && activeChat && (
                  <DropView>
                    <DropText>Drop files here to send them</DropText>
                  </DropView>
                )}
                <MessageListBody
                  activeChat={activeChat}
                  userId={userId}
                  showGallery={showGallery}
                  setReplyMessage={setReplyMessage}
                />
                <MessageInput
                  userId={userId}
                  activeChat={activeChat}
                  replyMessage={replyMessage}
                  setReplyMessage={setReplyMessage}
                />
                <input {...getInputProps()} />
              </DropContainer>
            )}
          </Dropzone>
        </MessageList>
      </Static>
      <GroupInfoContainer
        style={{
          width: isGroupInfoVisible ? 290 : 0,
          opacity: isGroupInfoVisible ? 1 : 0,
        }}
      >
        {isGroupInfoVisible && <GroupInfo groupUsers={groupUsers} userId={userId} />}
      </GroupInfoContainer>
      <Gallery
        galleryImagePath={galleryImagePath}
        isGalleryVisible={isGalleryVisible}
        closeGallery={closeGallery}
      />
    </Container>
  );
};

export default Chat;
